import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCog, Cloud, Hand, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProviderFilter } from "@/hooks/useProviderFilter";
import { useToast } from "@/hooks/use-toast";

/**
 * Inline control to assign a patient to a provider.
 * Shows IntakeQ as source of truth by default; manual assignment overrides.
 */
export default function PatientAssignControl({ patientEmail }) {
  const ctx = useProviderFilter();
  const { toast } = useToast();
  if (!ctx || !patientEmail) return null;

  const patient = ctx.patients.find(p => (p.email || "").toLowerCase() === patientEmail.toLowerCase());
  if (!patient) return null;

  const isManual = patient.assigned_provider_source === "manual";

  const handleChange = async (value) => {
    try {
      await ctx.assign(patient.id, value === "__none__" ? null : value);
      toast({
        title: value === "__none__" ? "Reverted to IntakeQ" : "Manually assigned",
        description: value === "__none__"
          ? `${patient.full_name} will sync from IntakeQ on next refresh.`
          : `${patient.full_name} manually assigned (override).`,
        duration: 3000,
      });
    } catch (err) {
      toast({ title: "Couldn't update assignment", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };

  const revertToIntakeQ = async () => {
    await handleChange("__none__");
  };

  return (
    <div className="bg-accent/40 border border-border rounded-xl p-3 space-y-2" data-testid="patient-assign-control">
      <div className="flex items-center gap-2">
        <UserCog className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <p className="text-xs text-muted-foreground shrink-0">Assigned provider:</p>
        <span className={`inline-flex items-center gap-1 text-[10px] rounded-full px-1.5 py-0.5 font-medium border shrink-0 ${
          isManual ? "text-amber-700 bg-amber-50 border-amber-200" : "text-green-700 bg-green-50 border-green-200"
        }`} data-testid="assign-source-badge">
          {isManual ? <><Hand className="w-2.5 h-2.5" /> Manual</> : <><Cloud className="w-2.5 h-2.5" /> IntakeQ</>}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Select value={patient.assigned_provider_id || "__none__"} onValueChange={handleChange}>
          <SelectTrigger className="h-7 text-xs flex-1" data-testid="assign-provider-select">
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              {isManual ? "Clear override (revert to IntakeQ)" : "Unassigned"}
            </SelectItem>
            {ctx.providers.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.full_name}{p.is_super_admin ? " (Super)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isManual && (
          <Button
            size="sm" variant="outline" className="h-7 text-xs rounded-full gap-1"
            onClick={revertToIntakeQ}
            data-testid="revert-to-intakeq-button"
          >
            <RotateCcw className="w-3 h-3" /> IntakeQ
          </Button>
        )}
      </div>
    </div>
  );
}
